import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, MapPin, Boxes } from 'lucide-react';
import { api } from '@/lib/api';

const HeroSearch = () => {
  const navigate = useNavigate();
  const [type, setType] = useState('sell');
  const [categoryId, setCategoryId] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [q, setQ] = useState('');
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    api.get('/categories', { params: { listing_type: type, parent_id: 'null' } })
      .then(r => setCategories(r.data))
      .catch(() => setCategories([]));
  }, [type]);

  useEffect(() => {
    api.get('/locations/states').then(r => setStates(r.data)).catch(() => setStates([]));
  }, []);

  useEffect(() => {
    if (!state) { setCities([]); return; }
    api.get('/locations/cities', { params: { state } }).then(r => setCities(r.data)).catch(() => setCities([]));
  }, [state]);

  const submit = () => {
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    if (city) params.set('city', city);
    if (q) params.set('q', q);
    const qs = params.toString();
    // If a category is chosen, the destination is its clean canonical taxonomy
    // URL (never ?category_id=UUID); remaining fields ride along as filters.
    if (categoryId) {
      const cat = categories.find(c => c.id === categoryId);
      const target = cat && (cat.url_path || (cat.listing_type && cat.path_slug ? `/category/${cat.listing_type}/${cat.path_slug}` : null));
      if (target) { navigate(qs ? `${target}?${qs}` : target); return; }
    }
    // Services live at /services (not /service) — everything else at /{type}.
    const path = type === 'service' ? '/services' : `/${type}`;
    navigate(qs ? `${path}?${qs}` : path);
  };

  return (
    <div className='indus-card relative overflow-hidden' data-testid='home-hero-search'>
      <div className='p-4 sm:p-6 lg:p-7 space-y-4'>
        <Tabs value={type} onValueChange={setType}>
          <TabsList data-testid='home-hero-listing-type-tabs' className='w-full grid grid-cols-4 max-w-md'>
            <TabsTrigger value='sell' data-testid='home-hero-tab-sell'>Sell</TabsTrigger>
            <TabsTrigger value='wanted' data-testid='home-hero-tab-wanted'>Wanted</TabsTrigger>
            <TabsTrigger value='rent' data-testid='home-hero-tab-rent'>Rent</TabsTrigger>
            <TabsTrigger value='service' data-testid='home-hero-tab-service'>Services</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
          <div className='md:col-span-4'>
            <label className='text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1'><Boxes className='h-3.5 w-3.5' /> Category</label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger data-testid='home-hero-category-select'>
                <SelectValue placeholder='All categories' />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='md:col-span-4'>
            <label className='text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1'><MapPin className='h-3.5 w-3.5' /> State</label>
            <Select value={state} onValueChange={(v) => { setState(v); setCity(''); }}>
              <SelectTrigger data-testid='home-hero-state-select'>
                <SelectValue placeholder='All states' />
              </SelectTrigger>
              <SelectContent>
                {states.map(s => (
                  <SelectItem key={s.state} value={s.state}>{s.state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='md:col-span-4'>
            <label className='text-xs font-medium text-muted-foreground mb-1'>City</label>
            <Select value={city} onValueChange={setCity} disabled={!state}>
              <SelectTrigger data-testid='home-hero-city-select'>
                <SelectValue placeholder={state ? 'All cities' : 'Pick state first'} />
              </SelectTrigger>
              <SelectContent>
                {cities.map(c => (
                  <SelectItem key={c.id} value={c.city}>{c.city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className='flex gap-2 items-center'>
          <Search className='h-4 w-4 text-muted-foreground shrink-0' />
          <Input
            data-testid='home-hero-keyword-input'
            placeholder='Search by keyword: e.g. CNC machine, forklift, bearing...'
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            className='h-11 flex-1'
          />
          <Button className='h-11 px-6 whitespace-nowrap shrink-0' onClick={submit} data-testid='home-hero-search-button'>
            <Search className='h-4 w-4 mr-1.5 shrink-0' /> Search
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HeroSearch;
